/**
 * Webchat da Digisac dentro do backoffice: o mesmo atendimento do WhatsApp e do
 * /helpdesk do PDV, aberto na própria tela; minimizado, some e volta pelo fone
 * do topo. Espelha o `util/suporte/webchatSuporte.js` do sischef-app (o
 * `WEBCHAT_ID` de lá fica em `util/suporte/suporteApp.js`). Os projetos não
 * compartilham pacote, então id, origem e horário mudam nos dois juntos.
 */

import { z } from 'zod';

export const ORIGEM_WEBCHAT = 'https://webchat.digisac.app';
export const SCRIPT_WEBCHAT = `${ORIGEM_WEBCHAT}/embedded.js`;
export const WEBCHAT_ID = '3f6dc37d-d763-4465-b310-01f0d291d626';

interface ConfigDigisac {
  id: string;
  payload?: { startOpen?: boolean };
}

declare global {
  interface Window {
    _digisac?: ConfigDigisac;
  }
}

export const ID_ESTILO_WEBCHAT = 'suporte-webchat-minimizado';
// O minimizado (85×85) cobria botões do canto, inclusive em notebook; reabre pelo fone do topo.
export const CSS_WEBCHAT_MINIMIZADO = '.close-widgets-container { visibility: hidden !important; pointer-events: none !important; }';

function garantirEstiloWebchat(): void {
  if (document.getElementById(ID_ESTILO_WEBCHAT)) return;
  const estilo = document.createElement('style');
  estilo.id = ID_ESTILO_WEBCHAT;
  estilo.textContent = CSS_WEBCHAT_MINIMIZADO;
  document.head.append(estilo);
}

/**
 * Só monta no clique e já aberto: carregado com a página, o widget abria
 * sozinho por cima da tela. Um segundo script daria dois widgets.
 */
export function montarWebchat(aoFalhar?: () => void): HTMLScriptElement {
  garantirEstiloWebchat();
  const existente = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_WEBCHAT}"]`);
  if (existente) return existente;

  window._digisac = { id: WEBCHAT_ID, payload: { startOpen: true } };
  const script = document.createElement('script');
  script.src = SCRIPT_WEBCHAT;
  script.async = true;
  if (aoFalhar) {
    script.onerror = () => {
      script.remove();
      aoFalhar();
    };
  }
  document.body.append(script);
  return script;
}

/** O botão pode remontar, mas o widget fica no body: quem decide se já existe é o DOM. */
export function webchatMontado(): boolean {
  return document.querySelector(`script[src="${SCRIPT_WEBCHAT}"]`) !== null;
}

/** Widget já montado e minimizado: pede para abrir de novo; false se o quadro ainda não apareceu. */
export function reabrirWebchat(): boolean {
  const quadro = document.querySelector<HTMLIFrameElement>('iframe.digisac-widget');
  if (!quadro?.contentWindow) return false;
  quadro.contentWindow.postMessage({ toggle: true }, ORIGEM_WEBCHAT);
  return true;
}

const MensagemWebchat = z.object({ c: z.enum(['open-widgets-container', 'close-widgets-container']) });

/** Classe que o quadro manda o embedded.js pôr no container (aberto/minimizado); o resto vira null. */
function lerSinalWebchat(evento: MessageEvent<unknown>): z.infer<typeof MensagemWebchat>['c'] | null {
  if (evento.origin !== ORIGEM_WEBCHAT || typeof evento.data !== 'string') return null;
  let dado: unknown;
  try {
    dado = JSON.parse(evento.data);
  } catch {
    return null;
  }
  const lido = MensagemWebchat.safeParse(dado);
  return lido.success ? lido.data.c : null;
}

const scriptDoWebchat = (): HTMLScriptElement | null => document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_WEBCHAT}"]`);

/** 'minimizou' só depois de o chat ter aberto e se o aviso ainda não foi dado; as marcas ficam no script, que dura a página. */
export function registrarSinalDoChat(evento: MessageEvent<unknown>): 'abriu' | 'minimizou' | null {
  const sinal = lerSinalWebchat(evento);
  const script = scriptDoWebchat();
  if (!sinal || !script) return null;
  if (sinal === 'open-widgets-container') {
    script.dataset.chatAbriu = 'sim';
    return 'abriu';
  }
  return script.dataset.chatAbriu === 'sim' && script.dataset.avisouMinimizado !== 'sim' ? 'minimizou' : null;
}

/** Uma vez por carregamento de página. */
export function marcarAvisoMinimizadoDado(): void {
  const script = scriptDoWebchat();
  if (script) script.dataset.avisouMinimizado = 'sim';
}

const emMinutos = (hora: number, minuto: number): number => hora * 60 + minuto;

const SEMANA_UTIL: Array<[number, number]> = [[emMinutos(8, 45), emMinutos(22, 0)]];

/** Grade do atendimento humano por dia da semana (0 = domingo), em minutos do dia. */
const GRADE_ATENDIMENTO: Record<number, Array<[number, number]>> = {
  0: [[emMinutos(11, 0), emMinutos(12, 0)]],
  1: SEMANA_UTIL,
  2: SEMANA_UTIL,
  3: SEMANA_UTIL,
  4: SEMANA_UTIL,
  5: SEMANA_UTIL,
  6: [
    [emMinutos(10, 0), emMinutos(13, 0)],
    [emMinutos(18, 0), emMinutos(20, 0)],
  ],
};

/** Data inválida conta como fora do horário em vez de estourar. */
export function atendimentoHumanoDisponivel(agora: Date): boolean {
  if (Number.isNaN(agora.getTime())) return false;
  const minutoDoDia = emMinutos(agora.getHours(), agora.getMinutes());
  const faixas = GRADE_ATENDIMENTO[agora.getDay()] ?? [];
  return faixas.some(([inicio, fim]) => minutoDoDia >= inicio && minutoDoDia <= fim);
}
